import { firestore } from '../../firebase';
import { collection, where, onSnapshot, query, documentId } from 'firebase/firestore';
import { supabase } from '../../lib/supabase';
import { sanitizeQrForClient } from '../sanitizeQrForClient';

export function getLiquidListener(org_id, setLiquidQRs, setDeletedQrs, onLoad) {
    if (!org_id) return;
    
    // Cache for processed QRs to avoid reprocessing unchanged documents
    let processedQRsCache = new Map();
    let processedDeletedQRsCache = new Map();
    let hasInitiallyLoaded = false;
    
    const processQR = (code) => {
        const obj = sanitizeQrForClient(code.data());
        const docUpdateTime = code.updateTime?.toMillis() || Date.now();
        
        // Only add timestamp if image exists - use document update time for consistent cache-busting
        const imageUrl = obj.qr_style.image ? 
            `${obj.qr_style.image}?${docUpdateTime}` : '';
        
        obj.qr_style = {
            ...obj.qr_style,
            type: 'svg',
            width: 128,
            height: 128,
            image: imageUrl,
            imageOptions: {
                ...obj.qr_style.imageOptions,
                imageSize: 0.4
            }
        };
        
        return { id: code.id, data: obj };
    };
    
    try {
        const userQuery = query(collection(firestore, 'codes'), where('org_id', '==', org_id));
        const liquidListener = onSnapshot(userQuery, 
            snapshot => {
                const changes = snapshot.docChanges();
                const isInitialLoad = changes.length === snapshot.docs.length;
                
                if (isInitialLoad) {
                    // Initial load: process all documents into active and deleted caches
                    const activeArr = snapshot.docs
                        .filter(code => code?.data()?.active === true)
                        .map(code => {
                            const processed = processQR(code);
                            processedQRsCache.set(code.id, processed);
                            return processed;
                        });

                    const deletedArr = snapshot.docs
                        .filter(code => code?.data()?.active === false)
                        .map(code => {
                            const processed = processQR(code);
                            processedDeletedQRsCache.set(code.id, processed);
                            return processed;
                        });

                    const sortedActive = activeArr.sort((a,b) => (a?.data?.nickname || '').toLowerCase().localeCompare((b?.data?.nickname || '').toLowerCase()));
                    const sortedDeleted = deletedArr.sort((a,b) => (a?.data?.nickname || '').toLowerCase().localeCompare((b?.data?.nickname || '').toLowerCase()));
                    setLiquidQRs(sortedActive);
                    setDeletedQrs(sortedDeleted);
                    
                    // Signal that initial load is complete
                    if (!hasInitiallyLoaded && onLoad) {
                        hasInitiallyLoaded = true;
                        onLoad();
                    }
                } else {
                    // Incremental update: only process changed documents
                    changes.forEach(change => {
                        const doc = change.doc;
                        
                        if (change.type === 'removed') {
                            processedQRsCache.delete(doc.id);
                            processedDeletedQRsCache.delete(doc.id);
                        } else if (doc.data()?.active === true) {
                            const processed = processQR(doc);
                            processedQRsCache.set(doc.id, processed);
                            processedDeletedQRsCache.delete(doc.id);
                        } else if (doc.data()?.active === false) {
                            const processed = processQR(doc);
                            processedDeletedQRsCache.set(doc.id, processed);
                            processedQRsCache.delete(doc.id);
                        } else {
                            // QR became inactive
                            processedQRsCache.delete(doc.id);
                            processedDeletedQRsCache.delete(doc.id);
                        }
                    });
                    
                    // Get all QRs from caches
                    const activeList = Array.from(processedQRsCache.values());
                    const deletedList = Array.from(processedDeletedQRsCache.values());
                    const sortedActive = activeList.sort((a,b) => (a?.data?.nickname || '').toLowerCase().localeCompare((b?.data?.nickname || '').toLowerCase()));
                    const sortedDeleted = deletedList.sort((a,b) => (a?.data?.nickname || '').toLowerCase().localeCompare((b?.data?.nickname || '').toLowerCase()));
                    setLiquidQRs(sortedActive);
                    setDeletedQrs(sortedDeleted);
                }
            },
            err => {
                console.error('Error in liquid listener:', err);
                setLiquidQRs([]);
                setDeletedQrs([]);
            }
        );
        return liquidListener;
    } catch (err) {
        console.error('Failed to set up liquid listener:', err);
        return () => {};
    };
};

export function getOrganization(org_id, setOrgObj, onLoad) {
    if (!org_id) return;
    let hasInitiallyLoaded = false;
    
    try {
        // Query organizations collection instead of users
        const orgQuery = query(collection(firestore, 'organizations'), where(documentId(), '==', org_id));
        const orgListener = onSnapshot(orgQuery, 
            snapshot => {
                snapshot.forEach(obj => {
                    if (obj.exists()) {
                        setOrgObj(obj.data());
                    } else {
                        setOrgObj(null);
                    }
                });
                
                // Signal that initial load is complete
                if (!hasInitiallyLoaded && onLoad) {
                    hasInitiallyLoaded = true;
                    onLoad();
                }
            },
            err => {
                console.error('Error in organization listener:', err);
                setOrgObj({});
            }
        );

        return orgListener;
    } catch(err) {
        console.error('Failed to set up organization listener:', err);
        return () => {};
    };
};

export const getWidgetsListener = async (org_id, setWidgets, onLoad) => {

    if (!org_id) return () => {};

    try {
        const { data: initialWidgets, error } = await supabase
            .from('cta_widgets')
            .select('*')
            .eq('org_id', org_id)

        if (error) {
            console.error("Error fetching initial widgets:", error);
            setWidgets([]);
        } else if (initialWidgets) {
            setWidgets(initialWidgets);
        }
        
        // Signal that initial load is complete
        if (onLoad) {
            onLoad();
        }
    } catch (err) {
        console.error('Initial fetch failed:', err);
        setWidgets([]);
        if (onLoad) {
            onLoad(); // Still call onLoad even on error to prevent infinite loading
        }
    }

    // realtime listener
    const channel = supabase
        .channel(`widgets_channel_${org_id}`)
        .on("postgres_changes",
            {
                event: "*",
                schema: "public",
                table: "cta_widgets",
                filter: `org_id=eq.${org_id}`
            },
            (payload) => {
                if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE' || payload.eventType === 'DELETE') {
                    
                    setWidgets(prevWidgets => {
                        if (payload.eventType === 'INSERT') {
                            return [...prevWidgets, payload.new];
                        } else if (payload.eventType === 'UPDATE') {
                            return prevWidgets.map(widget => 
                                widget.id === payload.new.id ? payload.new : widget
                            );
                        } else if (payload.eventType === 'DELETE') {
                            return prevWidgets.filter(widget => 
                                widget.id !== payload.old.id
                            );
                        }
                        return prevWidgets;
                    });
                }
            }
        )
        .subscribe()

    const handleRemoveChannel = async () => await supabase.removeChannel(channel);
    return handleRemoveChannel;
};
